import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { Link } from 'react-router-dom';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9tbtm77mpqf8k8iif7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const webPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': '关于我们 - 四虎影院',
    'description': '四虎影院品牌介绍、用户口碑评价及联系方式。海量高清影视资源免费在线观看平台。',
    'url': 'https://realsmodel.cn/about',
    'inLanguage': 'zh-CN',
  }), []);

  const faqSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      { '@type': 'Question', 'name': '四虎影院需要注册吗？', 'acceptedAnswer': { '@type': 'Answer', 'text': '不需要！四虎影院完全免费，无需注册登录即可在线观看所有影视资源。打开网站就能看，方便快捷。' } },
      { '@type': 'Question', 'name': '支持哪些设备观看？', 'acceptedAnswer': { '@type': 'Answer', 'text': '四虎影院支持电脑、平板、手机等多种设备。只需使用浏览器访问我们的网站，即可在任何设备上畅享观影体验。' } },
      { '@type': 'Question', 'name': '影视资源多久更新一次？', 'acceptedAnswer': { '@type': 'Answer', 'text': '我们每天都会更新最新影视资源，包括最新上映的电影、热播电视剧、综艺节目和动漫作品，确保您能第一时间观看到最新内容。' } },
      { '@type': 'Question', 'name': '播放卡顿怎么办？', 'acceptedAnswer': { '@type': 'Answer', 'text': '四虎影院支持多线路播放，如果当前线路卡顿，可以尝试切换其他播放线路。同时建议检查您的网络连接是否稳定。' } },
      { '@type': 'Question', 'name': '画质可以调节吗？', 'acceptedAnswer': { '@type': 'Answer', 'text': '可以！我们支持多种画质选择，包括高清、超清等。您可以根据网络状况和设备性能选择合适的画质。' } },
      { '@type': 'Question', 'name': '可以下载视频吗？', 'acceptedAnswer': { '@type': 'Answer', 'text': '四虎影院主要提供在线观看服务，暂不支持视频下载功能。我们建议您收藏喜欢的影片，随时在线观看。' } },
    ],
  }), []);

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://realsmodel.cn/' },
      { '@type': 'ListItem', 'position': 2, 'name': '关于我们', 'item': 'https://realsmodel.cn/about' },
    ],
  }), []);

  return (
    <>
      <SchemaOrg data={webPageSchema} />
      <SchemaOrg data={faqSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="bg-background-950 pt-20 pb-12">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <span>/</span>
              <span className="text-foreground-200">关于我们</span>
            </nav>
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-50 mb-4">
                关于四虎影院
              </h1>
              <p className="text-foreground-400 leading-relaxed">
                四虎影院致力于为广大影迷提供海量高清影视资源，涵盖电影、电视剧、综艺、动漫等多种类型。
                我们坚持免费、高清、流畅的观影体验，无需注册即可在线观看，是您追剧观影的首选平台。
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          {/* Brand Introduction */}
          <section className="py-12 md:py-16">
            <h2 className="text-2xl font-bold text-foreground-950 mb-8 flex items-center gap-2">
              <i className="ri-building-4-line text-primary-500"></i>
              品牌介绍
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="p-6 rounded-xl bg-background-100/50 border border-background-200/60">
                  <h3 className="text-lg font-semibold text-foreground-950 mb-3">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    四虎影院成立于2020年，专注于为用户提供高质量的在线影视观看服务。我们汇集了来自全球的优质影视资源，
                    涵盖电影、电视剧、综艺、动漫等多个类别，致力于打造国内领先的免费在线影视平台。
                  </p>
                </div>
                <div className="p-6 rounded-xl bg-background-100/50 border border-background-200/60">
                  <h3 className="text-lg font-semibold text-foreground-950 mb-3">核心优势</h3>
                  <ul className="space-y-3">
                    {[
                      { icon: 'ri-checkbox-circle-line', text: '资源丰富：拥有数万部影视资源，涵盖各类题材' },
                      { icon: 'ri-checkbox-circle-line', text: '高清画质：支持1080P超清播放，极致视觉体验' },
                      { icon: 'ri-checkbox-circle-line', text: '多线路播放：智能切换播放线路，告别卡顿' },
                      { icon: 'ri-checkbox-circle-line', text: '每日更新：及时更新最新影视内容，追剧无忧' },
                      { icon: 'ri-checkbox-circle-line', text: '免费观看：无需注册登录，打开即可畅享' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground-600">
                        <i className={`${item.icon} text-primary-500 mt-0.5 shrink-0`}></i>
                        <span>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden h-[400px] lg:h-auto">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20cinema%20theater%20interior%20with%20large%20screen%2C%20comfortable%20red%20seats%2C%20dark%20warm%20atmosphere%2C%20professional%20lighting%2C%20cinematic%20vibe%2C%20high%20quality%20photography&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="四虎影院品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-12 md:py-16 bg-background-100/50 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-[1400px] mx-auto">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 flex items-center gap-2">
                <i className="ri-star-line text-primary-500"></i>
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    name: '影迷小王',
                    rating: 5,
                    comment: '四虎影院真的太棒了！资源更新速度超快，画质也很清晰，追剧必备神器。最重要的是完全免费，强烈推荐！',
                    date: '2026-08-10',
                  },
                  {
                    name: '追剧达人',
                    rating: 5,
                    comment: '用了很多影视平台，四虎影院的体验是最好的。多线路播放很流畅，几乎没有卡顿，而且分类很清晰，找片方便。',
                    date: '2026-08-08',
                  },
                  {
                    name: '电影爱好者',
                    rating: 5,
                    comment: '在这里找到了很多冷门好片，资源真的很丰富。高清画质看大片太爽了，已经推荐给身边所有朋友了。',
                    date: '2026-08-05',
                  },
                  {
                    name: '动漫迷小陈',
                    rating: 5,
                    comment: '动漫资源很全，更新也快。每天下班回家第一件事就是打开四虎影院追番，生活离不开它了。',
                    date: '2026-08-03',
                  },
                  {
                    name: '剧荒患者',
                    rating: 4,
                    comment: '推荐算法做得不错，经常能发现合口味的剧。希望继续保持更新速度，支持四虎影院！',
                    date: '2026-07-30',
                  },
                  {
                    name: '老用户阿明',
                    rating: 5,
                    comment: '从2020年就开始用四虎影院了，见证了它一步步成长。现在的功能和资源比当年强太多了，会一直支持下去。',
                    date: '2026-07-28',
                  },
                ].map((review, i) => (
                  <div
                    key={i}
                    className="p-5 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200/50 transition-all duration-300"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                        {review.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-950">{review.name}</div>
                        <div className="text-xs text-foreground-400">{review.date}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 mb-3">
                      {Array.from({ length: 5 }, (_, j) => (
                        <i
                          key={j}
                          className={`text-xs ${j < review.rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      &ldquo;{review.comment}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-12 md:py-16">
            <h2 className="text-2xl font-bold text-foreground-950 mb-8 flex items-center gap-2">
              <i className="ri-customer-service-2-line text-primary-500"></i>
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@realsmodel.cn' },
                  { icon: 'ri-wechat-line', label: '微信公众号', value: '四虎影院' },
                  { icon: 'ri-weibo-line', label: '官方微博', value: '@四虎影院官方' },
                  { icon: 'ri-qq-line', label: 'QQ交流群', value: '893756421' },
                  { icon: 'ri-time-line', label: '客服时间', value: '7×24小时在线' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-background-100/50 border border-background-200/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">{item.label}</div>
                      <div className="text-sm font-medium text-foreground-950">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-6 rounded-xl bg-background-100/50 border border-background-200/60">
                <h3 className="text-lg font-semibold text-foreground-950 mb-5">给我们留言</h3>
                <form id="contact-form" data-readdy-form="" className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      required
                    />
                  </div>
                  <div className="honeypot-field">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="请输入您的留言内容（最多500字）"
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                  {formStatus === 'success' && (
                    <div className="mt-3 p-3 rounded-lg bg-accent-100/50 border border-accent-200/50 text-sm text-accent-700 text-center">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      留言已发送，感谢您的反馈！
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mt-3 p-3 rounded-lg bg-primary-100/50 border border-primary-200/50 text-sm text-primary-700 text-center">
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 md:py-16 bg-background-100/50 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-[1400px] mx-auto">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 flex items-center gap-2">
                <i className="ri-question-line text-primary-500"></i>
                常见问题
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                {[
                  {
                    q: '四虎影院需要注册吗？',
                    a: '不需要！四虎影院完全免费，无需注册登录即可在线观看所有影视资源。打开网站就能看，方便快捷。',
                  },
                  {
                    q: '支持哪些设备观看？',
                    a: '四虎影院支持电脑、平板、手机等多种设备。只需使用浏览器访问我们的网站，即可在任何设备上畅享观影体验。',
                  },
                  {
                    q: '影视资源多久更新一次？',
                    a: '我们每天都会更新最新影视资源，包括最新上映的电影、热播电视剧、综艺节目和动漫作品，确保您能第一时间观看到最新内容。',
                  },
                  {
                    q: '播放卡顿怎么办？',
                    a: '四虎影院支持多线路播放，如果当前线路卡顿，可以尝试切换其他播放线路。同时建议检查您的网络连接是否稳定。',
                  },
                  {
                    q: '画质可以调节吗？',
                    a: '可以！我们支持多种画质选择，包括高清、超清等。您可以根据网络状况和设备性能选择合适的画质。',
                  },
                  {
                    q: '可以下载视频吗？',
                    a: '四虎影院主要提供在线观看服务，暂不支持视频下载功能。我们建议您收藏喜欢的影片，随时在线观看。',
                  },
                ].map((faq, i) => (
                  <div
                    key={i}
                    className="p-5 rounded-xl bg-background-50 border border-background-200/60"
                  >
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2 flex items-start gap-2">
                      <span className="text-primary-500 font-bold">Q:</span>
                      {faq.q}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed flex items-start gap-2">
                      <span className="text-accent-500 font-bold">A:</span>
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}